import { useNavigate } from "@tanstack/react-router"
import { Badge } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { NoData } from "design-system/molecules/NoData"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "design-system/molecules/Table"
import { Plus, Tags } from "lucide-react"
import { useCanonicalTopicsGroupedQuery } from "modules/dashboard/hooks/useCanonicalTopicsGroupedQuery"
import { useTreemapConfig } from "modules/dashboard/hooks/useTreemapConfig"
import ShinyText from "modules/shared/components/Miscelanea/ShinyText"
import { PresenceScore } from "modules/shared/components/Scores/PresenceScore"
import { SentimentScore } from "modules/shared/components/Scores/SentimentScore"
import { SolveRateScore } from "modules/shared/components/Scores/SolveRateScore"
import { UrgencyScore } from "modules/shared/components/Scores/UrgencyScore"
import { formatCanonicalTopicLabel } from "modules/shared/lib/formatCanonicalTopicLabel"
import { getFulfillmentColor, getQuestionSolveRateColor } from "modules/shared/lib/sentiment"
import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  CanonicalTopicFilterBy,
  CanonicalTopicGroupedBy,
  CanonicalTopicGroupedByEnum,
  ClusterDomain,
  GoalType,
  GoalTypeEnum,
  TimeFrame,
  UNCATEGORIZED_CANONICAL_TOPIC_ID,
} from "schema"
import { ColorByEnum } from "modules/dashboard/lib/colorBy"

type CanonicalTopicRow = {
  id: string
  label: string
  description: string | null
  customersCount: number
  totalItems: number
  snapshotCount: number
  snapshotLabels: string[] | null
  snapshotIds: string[]
  percentage: number
  customerPercentage: number
  avgSentiment: number | null
  avgSolveRate: number | null
  avgPotentialSolveRate: number | null
  rings?: 1 | 2 | 3 | null
  groupId: string
  groupName: string
}

type Props = {
  domain: ClusterDomain
  groupedBy: CanonicalTopicGroupedBy
  tagBySentiment?: boolean
  goalId?: string | null
  goalType?: GoalType
  timeFrame: TimeFrame
  filterBy?: CanonicalTopicFilterBy
  groupNonCategoryTopics?: boolean
  title?: string | React.ReactNode
  description?: string
}

export const GroupedTreemapTable = ({
  domain,
  timeFrame,
  groupedBy: groupedByProp,
  tagBySentiment = true,
  goalId,
  goalType,
  filterBy,
  groupNonCategoryTopics = false,
}: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [showAll, setShowAll] = useState(false)

  const config = useTreemapConfig(domain, goalId ?? null, tagBySentiment)

  const { data: queryData, isPending } = useCanonicalTopicsGroupedQuery({
    query: {
      domain: config.domain,
      goalId: config.goalId,
      groupedBy: groupedByProp,
      timeFrame,
      filterBy,
      groupNonCategoryTopics,
    },
  })

  const { data } = queryData ?? {}
  const isGenerating = queryData?.isGenerating ?? false

  const noData =
    (isPending && !data) ||
    (data && (data.length === 0 || data.every((d) => d.canonicalTopics.length === 0)))

  const tableData = useMemo(() => {
    if (noData || !data) return []

    const grandTotal = data.reduce(
      (acc, group) =>
        acc + group.canonicalTopics.reduce((sum, topic) => sum + (topic.totalItems ?? 0), 0),
      0,
    )

    const totalCustomers = data.reduce(
      (acc, group) =>
        acc + group.canonicalTopics.reduce((sum, topic) => sum + (topic.customersCount ?? 0), 0),
      0,
    )

    const allTopics: Array<{
      topic: NonNullable<typeof data>[number]["canonicalTopics"][number]
      groupId: string
      groupName: string
    }> = []

    data.forEach((group) => {
      group.canonicalTopics.forEach((topic) => {
        allTopics.push({
          topic,
          groupId: group.groupId.toString(),
          groupName: group.groupName,
        })
      })
    })

    const sortedTopics = allTopics.sort(
      (a, b) => (b.topic.totalItems ?? 0) - (a.topic.totalItems ?? 0),
    )

    const rows: CanonicalTopicRow[] = sortedTopics.map(({ topic, groupId, groupName }, index) => {
      let rings: 1 | 2 | 3 | undefined = config.getRingsFromNode?.(topic)

      if (rings === undefined && config.colorBy === ColorByEnum.Presence) {
        const totalTopics = sortedTopics.length
        const rank = index + 1
        if (rank <= totalTopics / 3) {
          rings = 3
        } else if (rank <= (totalTopics * 2) / 3) {
          rings = 2
        } else {
          rings = 1
        }
      }

      return {
        id: topic.id,
        label: formatCanonicalTopicLabel(topic.label),
        description: topic.description,
        customersCount: topic.customersCount ?? 0,
        totalItems: topic.totalItems ?? 0,
        snapshotCount: topic.snapshotCount,
        snapshotLabels: topic.snapshotLabels ?? null,
        snapshotIds: (topic.snapshotIds ?? []).map(String),
        percentage: grandTotal > 0 ? ((topic.totalItems ?? 0) / grandTotal) * 100 : 0,
        customerPercentage:
          totalCustomers > 0 ? ((topic.customersCount ?? 0) / totalCustomers) * 100 : 0,
        avgSentiment: topic.avgSentiment ?? null,
        avgSolveRate: topic.avgSolveRate ?? null,
        avgPotentialSolveRate: topic.avgPotentialSolveRate ?? null,
        rings,
        groupId,
        groupName,
      }
    })

    return rows
  }, [data, noData, config])

  const displayedData = useMemo(() => {
    const limit = showAll ? 10 : 5
    return tableData.slice(0, limit)
  }, [tableData, showAll])

  const scoreHeaderLabel = config.scoreLabel

  const handleRowClick = (row: CanonicalTopicRow) => {
    if (row.id === UNCATEGORIZED_CANONICAL_TOPIC_ID) return

    const segmentId =
      groupedByProp.type === CanonicalTopicGroupedByEnum.segmentGroup ? row.groupId : undefined

    const campaignId =
      groupedByProp.type === CanonicalTopicGroupedByEnum.campaign ? row.groupId : undefined

    const navigateTo = config.getNavigateTo?.({
      node: { id: parseInt(row.id), snapshotIds: row.snapshotIds.map(Number) },
      domain,
      segmentId,
      canonicalId: row.id,
      campaignId,
      goalId: goalId ?? undefined,
    })

    if (navigateTo) {
      navigate(navigateTo)
    }
  }

  const getScoreWithTooltip = (row: CanonicalTopicRow) => {
    const scoreNode = {
      avgSentiment: row.avgSentiment,
      avgSolveRate: row.avgSolveRate,
      avgPotentialSolveRate: row.avgPotentialSolveRate,
      rings: row.rings,
    }

    const fullScoreComponent = config.getScoreComponent(scoreNode)

    let miniScoreComponent = null
    switch (config.colorBy) {
      case ColorByEnum.Sentiment:
        miniScoreComponent = <SentimentScore sentiment={row.avgSentiment} mini />
        break
      case ColorByEnum.SolveRate:
        miniScoreComponent = <SolveRateScore solveRate={row.avgSolveRate} mini />
        break
      case ColorByEnum.QuestionSolveRate:
        miniScoreComponent = (
          <SolveRateScore
            solveRate={row.avgSolveRate}
            mini
            labelKey="shared.rates.answeredRate"
            getColor={getQuestionSolveRateColor}
          />
        )
        break
      case ColorByEnum.Urgency:
        miniScoreComponent = (
          <UrgencyScore
            highRate={row.avgSolveRate}
            highAndMediumRate={row.avgPotentialSolveRate}
            mini
          />
        )
        break
      case ColorByEnum.Fulfillment:
        miniScoreComponent = (
          <SolveRateScore
            solveRate={row.avgSolveRate}
            mini
            labelKey="shared.rates.fulfillmentRate"
            getColor={getFulfillmentColor}
          />
        )
        break
      case ColorByEnum.Presence:
        miniScoreComponent = <PresenceScore rings={row.rings} mini />
        break
      default:
        miniScoreComponent = fullScoreComponent
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-help">{miniScoreComponent}</div>
        </TooltipTrigger>
        <TooltipContent variant="card" className="p-4" sideOffset={16} side="right">
          <div className="flex flex-col items-center gap-1">{fullScoreComponent}</div>
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <div className="flex-1 min-w-0 h-full pb-4 relative">
      <div className="flex flex-col w-full gap-4 overflow-hidden h-full">
        {!noData && tableData.length > 0 ? (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  {goalType === GoalTypeEnum.Classification ? (
                    <TableHead className="w-full">{t("dashboard.table.classification")}</TableHead>
                  ) : (
                    <TableHead className="w-full">{t("dashboard.table.topic")}</TableHead>
                  )}
                  <TableHead className="min-w-60" align="right">
                    {t("dashboard.table.tags")}
                  </TableHead>
                  <TableHead className="min-w-60" align="right">
                    {t("dashboard.table.insights")}
                  </TableHead>
                  <TableHead className="min-w-60" align="right">
                    {t("dashboard.table.clients")}
                  </TableHead>
                  <TableHead className="min-w-60" align="right">
                    {scoreHeaderLabel}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayedData.map((row) => (
                  <TableRow
                    key={row.id}
                    className={
                      row.id === UNCATEGORIZED_CANONICAL_TOPIC_ID
                        ? "h-10 w-full"
                        : "cursor-pointer h-10 w-full"
                    }
                    onClick={() => handleRowClick(row)}
                  >
                    <TableCell>
                      <Tooltip>
                        <TooltipTrigger className="text-left truncate block">
                          <span className="font-medium">{row.label}</span>
                        </TooltipTrigger>
                        {row.description && (
                          <TooltipContent className="max-w-sm p-4" side="right" sideOffset={16}>
                            <p className="text-xs">{row.description}</p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TableCell>

                    <TableCell align="right">
                      {row.snapshotLabels && row.snapshotLabels.length > 0 ? (
                        <Tooltip>
                          <TooltipTrigger>
                            <Badge variant="outline" className="flex items-center gap-1.5">
                              <Tags className="size-3" />
                              <span className="text-xs">
                                {t("dashboard.table.tagsLabel", { count: row.snapshotCount })}
                              </span>
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent
                            variant="card"
                            className="p-3"
                            side="left"
                            sideOffset={16}
                          >
                            <div className="flex flex-wrap gap-1 max-w-sm">
                              {Array.from(new Set(row.snapshotLabels))
                                .slice(0, 8)
                                .map((label, idx) => (
                                  <Badge key={`${row.id}-${label}-${idx}`} variant="secondary">
                                    {label}
                                  </Badge>
                                ))}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <span className="text-muted-foreground text-sm">{"-"}</span>
                      )}
                    </TableCell>

                    <TableCell align="right">
                      <div className="flex flex-col items-end">
                        <span className="font-medium">
                          {row.percentage.toFixed(1)}
                          {"%"}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {t("dashboard.table.insightsLabel", { count: row.totalItems })}
                        </span>
                      </div>
                    </TableCell>

                    <TableCell align="right">
                      <div className="flex flex-col items-end">
                        <span className="font-medium">
                          {row.customerPercentage.toFixed(1)}
                          {"%"}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {t("dashboard.table.clientsLabel", { count: row.customersCount })}
                        </span>
                      </div>
                    </TableCell>

                    <TableCell align="right">
                      <div className="flex justify-end items-center">
                        {getScoreWithTooltip(row)}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {(isGenerating || (tableData.length > 5 && !showAll)) && (
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  {isGenerating && (
                    <ShinyText text={t("insights.openGoal.treemap.generatingData")} />
                  )}
                </div>
                <div className="flex items-center">
                  {tableData.length > 5 && !showAll && (
                    <Button variant="ghost" size="sm" onClick={() => setShowAll(true)}>
                      <Plus className="size-4 mr-1.5" />
                      {t("dashboard.table.seeAll")}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          !isPending && (
            <div className="h-[280px] flex flex-col items-center justify-center gap-2 text-center min-h-0 border-muted-foreground/5 border rounded p-2 my-0.5">
              <NoData title={config.noDataTitle} description={config.noDataDescription} />
            </div>
          )
        )}
      </div>
    </div>
  )
}
